import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow
} from '@angular/material/table';
import { CoreQuotingSystem } from '../../../model/quoting-system/core-quoting-system.model';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ToolbarService } from '../../../services/toolbar.service';
import { UserService } from '../../../services/user.service';
import { SysAdminService } from '../../../services/sys-admin.service';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { AddQuotingSystemComponent } from '../add-quoting-system/add-quoting-system.component';
import { QuotingSystemUpdate } from '../../../model/quoting-system/quoting-system-create.model';
import { ConfirmBoxComponent } from '../../../components/confirm-box/confirm-box.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ModalConfigsHelper } from '../../../helpers/modal-configs.helper';
import { smallModalConfig, wideModalConfig } from '../../../consts/modal-config.const';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { HighlightStatusDirective } from '../../../directives/highlight-status.directive';
import { MatExpansionPanelDescription } from '@angular/material/expansion';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';

@Component({
  selector: 'app-quoting-system-section',
  templateUrl: './quoting-system-section.component.html',
  styleUrls: ['./quoting-system-section.component.scss'],
  imports: [
    MatButton,
    MatDivider,
    MatIconButton,
    MatIcon,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    HighlightStatusDirective,
    MatExpansionPanelDescription,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow
  ]
})
export class QuotingSystemSectionComponent implements OnChanges, OnDestroy {
  @Input() quotingSystemList: CoreQuotingSystem[] = [];
  @Output() updateQuotingSystemList: EventEmitter<any> = new EventEmitter();
  @Output() confirmRevokeAllTokens: EventEmitter<any> = new EventEmitter();
  @Output() configureInterfaces: EventEmitter<any> = new EventEmitter();
  @ViewChild('quotingSystemSort', { static: true }) quotingSystemSort: MatSort;
  quotingSystemListDataSource: MatTableDataSource<CoreQuotingSystem>;
  quotingSystemExpanded = false;
  quotingSystemActiveColumn = 'companyName';
  quotingSystemListColumnHeaders = ['companyName', 'contactPhoneNumber', 'oAuth2Enabled', 'kebab'];
  private unsubscribe = new Subject<void>();

  constructor(
    private message: MessagingService,
    private editSysadmin: MatDialog,
    public toolbarService: ToolbarService,
    private userService: UserService,
    private sysAdminService: SysAdminService,
    private addQSystem: MatDialog,
    private confirmBox: MatDialog,
    private modalConfigsHelper: ModalConfigsHelper
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    this.quotingSystemListDataSource = new MatTableDataSource(changes.quotingSystemList.currentValue);
    this.quotingSystemListDataSource.sortingDataAccessor = this.sortingQuotingSystem;
    this.quotingSystemListDataSource.sort = this.quotingSystemSort;
  }

  addEditQuotingSystem(quotingSystem?: QuotingSystemUpdate) {
    const dialogConfig: MatDialogConfig = wideModalConfig;
    dialogConfig.data = quotingSystem ? { quotingSystem } : null;
    const dialogRef = this.addQSystem.open(AddQuotingSystemComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'Success') {
        this.message.confirm(`Quoting system ${quotingSystem ? 'Edited' : 'Added'}`);
        this.updateQuotingSystemList.emit();
      }
    });
  }

  deleteQuotingSystem(clientId: string) {
    this.toolbarService.setCloudState('SAVING');
    this.sysAdminService
      .deleteQuotingSystem(clientId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => this.toolbarService.setCloudState('RESTING'),
        error: () => {
          this.message.error('Quoting System could not be deleted.');
          this.toolbarService.setCloudState('RESTING');
        },
        complete: () => {
          this.message.confirm('Quoting System Deleted.');
          this.updateQuotingSystemList.emit();
        }
      });
  }

  confirmDeleteQuotingSystem(clientId: string) {
    const data = {
      title: 'Delete Quoting System',
      alertMessage: 'Quoting System will be deleted.',
      confirmButton: 'DELETE'
    };
    const dialogRef = this.confirmBox.open(
      ConfirmBoxComponent,
      this.modalConfigsHelper.buildMediumModalConfig(data, smallModalConfig)
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'DELETE') {
        this.toolbarService.setCloudState('FETCHING');
        this.deleteQuotingSystem(clientId);
      }
    });
  }

  sortingQuotingSystem(item, property) {
    if (property === 'eventNotifications') {
      if (item.notificationsEnabled) {
        if (item.eventNotifications.filter((element) => element.checked).length !== 0) {
          return item[property]
            .filter((element) => element.checked)
            .map((element) => element.eventType)
            .join();
        } else {
          return 'No Events selected';
        }
      } else {
        return 'Disabled';
      }
    }
    return item[property] ? item[property].toString().toLowerCase() : '';
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
